import * as uuid from 'uuid';
import type { WatchSource } from 'vue';
import { markRaw, reactive } from 'vue';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { NoraStateManager } from '#nora-ui/map/map/state-managers/NoraStateManager';
import { TypedReactiveMap } from '#nora-ui/map/map/TypedReactiveMap';
import type { NoraMapMarker } from '#nora-ui/map/types/marker.types';
import type { MarkerStateSnapshot, Persistable, SerializedMarker } from '#nora-ui/map/types/persistence.types';

export class MarkerStateManager extends NoraStateManager implements Persistable<MarkerStateSnapshot> {
  private markers = reactive(new TypedReactiveMap<string, NoraMapMarker>());

  constructor(private readonly map: NoraMap) {
    super();
  }

  addMarker(options: Omit<NoraMapMarker, 'id'>): string {
    const id = uuid.v4();
    // markRaw: the marker's OL style goes straight to a feature and must stay a plain object.
    const marker: NoraMapMarker = markRaw({
      ...options,
      id,
    });

    this.markers.set(id, marker);

    this.map.layers.marker.addMarkerFeature(marker);

    return id;
  }

  getMarker(id: string): NoraMapMarker | undefined {
    return this.markers.get(id);
  }

  getAllMarkers(): NoraMapMarker[] {
    return Array.from(this.markers.values());
  }

  removeMarker(id: string): void {
    this.markers.delete(id);
    this.map.layers.marker.removeMarkerFeature(id);
  }

  /**
   * Serialize the marker state to a plain object for persistence.
   * Note: The 'style' property is excluded as it's not JSON-serializable.
   */
  serialize(): MarkerStateSnapshot {
    const serializedMarkers: SerializedMarker[] = Array.from(this.markers.values()).map(
      ({ style: _style, ...rest }) => rest,
    );

    return {
      markers: serializedMarkers,
    };
  }

  /**
   * Restore marker state from a persisted snapshot.
   */
  deserialize(snapshot: MarkerStateSnapshot): void {
    // Clear existing markers
    this.markers.clear();

    // Restore markers from snapshot
    for (const marker of snapshot.markers) {
      this.markers.set(marker.id, markRaw(marker));
      this.map.layers.marker.addMarkerFeature(marker);
    }
  }

  /**
   * Returns a watch source for triggering persistence when state changes.
   */
  getWatchSource(): WatchSource {
    return () => Array.from(this.markers.entries());
  }

  /**
   * Load marker state. No-op since markers are added programmatically.
   */
  load(): void {
    // No-op - markers are managed programmatically via addMarker/removeMarker
  }

  /**
   * Unload/clear all marker state.
   */
  unload(): void {
    this.markers.clear();
  }
}
